import React from "react";
import { PageWrapper } from "../material-ledger/request-ml/material-ledger-summary";
import { Alert, AlertTitle, Box, CircularProgress, LinearProgress, Paper, Typography } from "@mui/material";
import { useAuditDataForm } from "./hooks/useAuditDataForm";
import { TemplateDownloadHeader } from "./components/TemplateDownloadHeader";
import { FileDropzone } from "./components/FileDropzone";
import { ValidationTimeline } from "./components/ValidationTimeline";
import { ServerErrorDetails } from "./components/ServerErrorDetails";

// Re-export type declarations for backward compatibility
export type {
  FieldErrorDetail,
  SheetErrorDetail,
  StructuredApiErrorResponse,
  ValidationTimelineStep,
} from "./types/audit-data.types";

const AuditDataForm: React.FC = () => {
  const {
    selectedFile,
    parsing,
    uploading,
    extractedData,
    timelineSteps,
    serverErrors,
    legacyErrors,
    successMsg,
    isDragging,
    fileInputRef,
    allTimelinePassed,
    notify,
    setSuccessMsg,
    resetState,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleUpload,
  } = useAuditDataForm();

  return (
    <PageWrapper>
      <Typography mt={4} variant="h6" gutterBottom align="center" fontWeight="600">
        Audit Data - File Upload
      </Typography>

      <Paper
        elevation={0}
        sx={{
          mt: 3,
          mx: "auto",
          maxWidth: 720,
          p: { xs: 2.5, sm: 4 },
          borderRadius: 3,
          backgroundColor: "#f2f3f5",
        }}
      >
        <Typography variant="body2" mb={2} color="text.secondary" align="center">
          Upload the completed Audit Data Excel template (.xlsx / .xls). It will be validated and
          processed automatically once uploaded.
        </Typography>

        {/* Download Sample Template Header Section */}
        <TemplateDownloadHeader />

        {/* Interactive Drag & Drop Box */}
        <FileDropzone
          fileInputRef={fileInputRef}
          selectedFile={selectedFile}
          uploading={uploading}
          parsing={parsing}
          isDragging={isDragging}
          onFileChange={handleFileChange}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onReset={resetState}
        />

        {/* Parsing Progress */}
        {parsing && (
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <CircularProgress size={28} sx={{ mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Parsing Excel workbook and extracting data…
            </Typography>
          </Box>
        )}

        {/* Validation Timeline Card */}
        {timelineSteps.length > 0 && !parsing && (
          <ValidationTimeline
            timelineSteps={timelineSteps}
            allTimelinePassed={allTimelinePassed}
            extractedData={extractedData}
            uploading={uploading}
            successMsg={successMsg}
            onUpload={handleUpload}
            onClear={resetState}
            notify={notify}
          />
        )}

        {/* Uploading Progress bar */}
        {uploading && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" mb={1} align="center">
              Validating &amp; Uploading Excel…
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {/* Success Alert */}
        {successMsg && !uploading && (
          <Alert severity="success" sx={{ mt: 3, borderRadius: 2 }}>
            <AlertTitle>Success</AlertTitle>
            {successMsg}
          </Alert>
        )}

        {/* Structured Server Validation Errors Display */}
        <ServerErrorDetails serverErrors={serverErrors} legacyErrors={legacyErrors} />
      </Paper>
    </PageWrapper>
  );
};

export default AuditDataForm;
